import { useCombobox } from "@mantine/core";
import clsx from "clsx";
import { useMemo } from "react";
import { PieChart } from "react-minimal-pie-chart";
import { Link } from "react-router-dom";

import { Site, SiteGoals } from "~/api/Api.ts";
import { Icon, IconProps } from "~/assets/icons/Icon.tsx";
import { useAuth } from "~/contexts/AuthProvider.tsx";
import { getChartTextColor } from "~/helpers/helpers.ts";
import { routes } from "~/router";
import { getBrandName } from "~/utils/white-label";

import styles from "./Optimization.module.scss";

type OptimizationProps = {
  site: Site;
  isSiteGoalsLoading: boolean;
  siteGoals: SiteGoals | {};
  goalsLabel: string;
  onLinkClick?: () => void;
};

const goalsConfig = {
  site_speed: {
    label: "Install AI Visibility Engine and Snippet",
    getPath: (siteId: number, domainId?: number) =>
      domainId
        ? routes.site.visibilityEngine.domain({ id: siteId, domainId })
        : {},
  },
  snippet: {
    label: "Install Snippet",
    getPath: (siteId: number) =>
      routes.site.settings.tab({ id: siteId, tabId: "snippet" }),
  },
  recommendations: {
    label: "Configure Recommendations",
    getPath: (siteId: number) => routes.site.onpage({ id: siteId }),
  },
  enhancement_customizations: {
    label: "Configure AI Enhancements",
    getPath: (siteId: number) =>
      routes.site.liveEditor.customizations({ id: siteId }),
  },
  keywords: {
    label: "Add Keywords to Track",
    getPath: (siteId: number) => routes.site.keywords.list({ id: siteId }),
  },
  schema: {
    label: "Configure Schema",
    getPath: (siteId: number) => routes.site.schema({ id: siteId }),
  },
  ai_customizations: {
    label: "Install and Customize Live Editor",
    getPath: (siteId: number) => routes.site.liveEditor.install({ id: siteId }),
  },
};

const statusToIconMap: Record<
  "complete" | "pending" | "incomplete" | string,
  IconProps["iconName"] | undefined
> = {
  complete: "GreenCheckmarkIcon",
  pending: "ClockIcon",
  incomplete: "GreyCheckmarkIcon",
  null: undefined,
};

const Optimization = ({
  siteGoals,
  site,
  goalsLabel,
  onLinkClick,
}: OptimizationProps) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });
  const { user } = useAuth();
  const currentUserName = useMemo(() => user?.first_name, [user]);

  const goalsArray = useMemo(() => {
    if (!siteGoals || !site) return [];

    return Object.entries(siteGoals).map(([key, status]) => ({
      key,
      status,
      label: goalsConfig[key as keyof typeof goalsConfig]?.label || key,
      path: goalsConfig[key as keyof typeof goalsConfig]?.getPath(
        site.id,
        site.delegated_domain?.id,
      ),
      iconName: statusToIconMap[status],
    }));
  }, [siteGoals, site]);

  return (
    <div className={styles.optimization}>
      <div className={styles.optimizationHeader}>
        <div className={styles.optimizationHeaderChart}>
          <PieChart
            lineWidth={15}
            label={() => goalsLabel}
            startAngle={270}
            viewBoxSize={[100, 100]}
            labelStyle={{
              fontSize: "1.5rem",
              fill: getChartTextColor(),
            }}
            labelPosition={0}
            data={Object.values(siteGoals)
              .sort(
                (a, b) =>
                  (b === "complete" ? 1 : 0) - (a === "complete" ? 1 : 0),
              )
              .map((item) => {
                return {
                  value: item === "complete" ? 16.6 : 16.6,
                  color: item !== "complete" ? "#F2F4F8" : "#047BF8",
                };
              })}
          />
        </div>
        {/*
         // @ts-expect-error TS2551 non-existing className */}
        <div className={styles.optimizationHeaderInfo}>
          Welcome to {getBrandName()}, {currentUserName}!
          <p className={styles.optimizationHeaderDescription}>
            Let's get started together
          </p>
        </div>
      </div>
      <div className={styles.optimizationItems}>
        {goalsArray.map((goal, index) => (
          <Link
            key={index}
            className={clsx(
              styles.optimizationItem,
              styles[goal.status as keyof typeof styles],
            )}
            to={goal.path || "#"}
            onClick={onLinkClick} // Add the onClick handler
          >
            {/*
             // @ts-expect-error TS2551 non-existing className */}
            <div className={styles.optimizationItemTitle}>{goal.label}</div>
            {goal.iconName && <Icon iconName={goal.iconName} />}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Optimization;
